import React, {
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
  MouseEventHandler,
} from 'react';
import ReactSelect, {
  components,
  OptionProps,
  OptionTypeBase,
  SingleValueProps,
  MultiValueProps,
  PlaceholderProps,
} from 'react-select';
import { MultiValueRemoveProps } from 'react-select/src/components/MultiValue';
import AsyncSelect, { Props as AsyncProps } from 'react-select/async';
import ExpandIndicator from '../ExpandIndicator/ExpandIndicator';
import { GroupTypeBase } from 'react-select/src/types';
import MaybeLink from '../MaybeLink/MaybeLink';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import useTheme, { ThemeContext } from '../../contexts/useTheme';
import { EnhancedMarkdownOptionsContextProvider } from '@resources/js/contexts/useEnhancedMarkdownOptions';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type ReactSelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean
> = ReactSelect<OptionType, IsMulti>['props'];

export type AsyncSelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean
> = AsyncProps<OptionType, IsMulti> & {
  async?: true;
  isSortable?: boolean;
  icon?: React.ReactElement;
};

export type SyncSelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean
> = ReactSelectProps<OptionType, IsMulti> & {
  async?: false;
  slim?: false;
  valueLabel?: string;
  defaultLevel?: number;
  borderLevel?: number;
  isCompact?: boolean;
  usesEnhancedMarkdown?: boolean;
  icon?: React.ReactElement;
  OptionWrapper?: ({
    option,
    children,
  }: {
    option: OptionType;
    children: React.ReactNode;
    isSortable?: boolean;
  }) => JSX.Element | null;
};

type CommonSelectProps =
  | { style?: SelectStyle }
  | {
      styles: SelectStyle[];
    };

export type SelectStyle =
  | 'menu'
  | 'large-link'
  | 'link'
  | 'dropdown'
  | 'header-search'
  | 'bold';

export type SelectProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean
> = CommonSelectProps &
  (
    | SyncSelectProps<OptionType, IsMulti>
    | AsyncSelectProps<OptionType, IsMulti>
  );

type DropdownIndicatorIconProps = {
  className?: string;
};

export const DropdownIndicatorIcon = ({
  className = '',
}: DropdownIndicatorIconProps): JSX.Element => (
  <i
    className={`link-select__dropdown-icon zmdi zmdi-caret-down ${className}`}
  />
);

const determineStylingProps = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean
>(
  props: SelectProps<OptionType, IsMulti>,
  theme: ThemeContext
): Partial<ReactSelectProps<OptionType, IsMulti>> => {
  let styles = theme.selectStyles;
  if (Array.isArray(props.styles) && props.styles?.length) {
    styles = props.styles;
  } else if (props.style) {
    styles = [props.style];
  }

  if (!styles?.length) {
    return {
      menuPortalTarget: document.body,
      styles: {
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
          color: 'black',
        }),
      },
    };
  }

  const valueLabel = props.valueLabel;

  if (styles.includes('header-search')) {
    return {
      className: `menu-select menu-select--async menu-select--header-search ${getStyleClassNames(
        styles,
        'header-search'
      )}`,
      classNamePrefix: 'menu-select',
      isSearchable: true,
      isClearable: false,
    };
  }

  if (styles.includes('menu')) {
    const isAsync = Boolean(props.async);
    const isSlim = Boolean(props.slim);
    const hasActions = props.options?.some(
      (option) => option.action !== undefined
    );

    return {
      className: `menu-select${isAsync ? ' menu-select--async' : ''}${
        isSlim ? ' menu-select--slim' : ''
      }${props.maxMenuHeight ? '' : ' menu-select--no-max-height'} ${
        props.isMulti ? 'menu-select--multi' : ''
      } ${
        props.isMulti && props.isSortable
          ? 'menu-select__multi-value--sortable'
          : ''
      } ${getStyleClassNames(styles, 'menu')}`,
      classNamePrefix: 'menu-select',
      isSearchable: isAsync,
      isClearable: isAsync || hasActions,
    };
  }

  if (styles.includes('large-link') || styles.includes('link')) {
    let className = `link-select ${getStyleClassNames(styles, [
      'large-link',
      'link',
    ])}`;

    if (styles.includes('large-link')) {
      className += ' link-select--large';
    }

    return {
      isClearable: false,
      className,
      classNamePrefix: 'link-select',
      components: {
        DropdownIndicator: (props) => (
          <components.DropdownIndicator<OptionType, IsMulti> {...props}>
            <DropdownIndicatorIcon />
          </components.DropdownIndicator>
        ),
        ...props.components,
      },
    };
  }

  if (styles.includes('dropdown')) {
    return {
      isClearable: false,
      className:
        `dropdown-select ${getStyleClassNames(styles, 'dropdown')}` +
        (props.defaultLevel
          ? ' dropdown-select--level-' + props.defaultLevel
          : '') +
        (props.borderLevel
          ? ' dropdown-select--border-level-' + props.borderLevel
          : '') +
        (props.isCompact ? ' dropdown-select--compact' : ''),
      classNamePrefix: 'dropdown-select',
      isSearchable: false,
      components: {
        DropdownIndicator: (props) => (
          <components.DropdownIndicator<OptionType, IsMulti> {...props}>
            <span tabIndex={-1}>
              <ExpandIndicator
                /* eslint-disable-next-line react/prop-types */
                isExpanded={props.selectProps.menuIsOpen ?? false}
                shouldIgnoreAccessiblity={true}
              />
            </span>
          </components.DropdownIndicator>
        ),
        SingleValue: (singleValueProps) => (
          <components.SingleValue {...singleValueProps}>
            {!valueLabel ? null : (
              <div className='dropdown-select__single-value-subtitle'>
                {valueLabel}
              </div>
            )}
            <div className='dropdown-select__single-value-title'>
              {singleValueProps.children}
            </div>
          </components.SingleValue>
        ),
      },
    };
  }

  return {};
};

const enhancedMarkdownOptions = {
  lazyLoad: false,
};

function getStyleClassNames(
  styles: SelectStyle[],
  mainStyle: string | string[]
): string {
  const mainStyles = Array.isArray(mainStyle) ? mainStyle : [mainStyle];

  return styles
    .filter((style) => !mainStyles.includes(style))
    .map((style) => `select--style-${style}`)
    .join(' ');
}

function onSelectKeyDown(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  document
    .querySelector('.menu-select__option--is-focused')
    ?.closest('a')
    ?.click();
}

function Select<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean = false
>(props: SelectProps<OptionType, IsMulti>): JSX.Element {
  const theme = useTheme();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const closeMenuTimeout = useRef<NodeJS.Timeout | null>(null);
  const onMenuClose = useCallback(() => {
    if (closeMenuTimeout.current) {
      clearTimeout(closeMenuTimeout.current);
    }
    closeMenuTimeout.current = setTimeout(() => {
      setMenuIsOpen(false);
    }, 200);
  }, []);

  let propsWithStylingApplied: Partial<ReactSelectProps<OptionType, IsMulti>> =
    {
      ...props,
      ...determineStylingProps(props, theme),
    };
  if (props.className) {
    propsWithStylingApplied = {
      ...propsWithStylingApplied,
      className: `${propsWithStylingApplied.className} ${props.className}`,
    };
  }

  const extraOptionProps = {
    usesEnhancedMarkdown: props.usesEnhancedMarkdown,
    Wrapper: props.OptionWrapper,
  };

  const hasOptionsWithUrl = props.options?.some(
    (option) =>
      option.url ||
      option.options?.some((subOption: OptionType) => subOption.url)
  );

  const hasActions = props.options?.some(
    (option) => option.action !== undefined
  );

  let allProps: Partial<ReactSelectProps<OptionType, IsMulti>> = {
    ...propsWithStylingApplied,
    components: {
      ...(propsWithStylingApplied.components ?? {}),
      Option: propsWithStylingApplied.components?.Option ?? Option,
      SingleValue:
        propsWithStylingApplied.components?.SingleValue ??
        (extraOptionProps.usesEnhancedMarkdown
          ? EnhancedMarkdownSingleValue
          : components.SingleValue),
      Placeholder: hasActions
        ? (
            placeholderProps: PlaceholderProps<
              OptionType,
              IsMulti,
              GroupTypeBase<OptionType>
            >
          ) => SingleValuePlaceholder({ ...placeholderProps, icon: props.icon })
        : components.Placeholder,
      MultiValue:
        props.isMulti && props.isSortable
          ? SortableMultiValue
          : components.MultiValue,
      MultiValueRemove:
        props.isMulti && props.isSortable
          ? SortableMultiValueRemove
          : components.MultiValueRemove,
    },
    ...(hasOptionsWithUrl
      ? {
          menuIsOpen: props.menuIsOpen ?? menuIsOpen,
          onMenuOpen: () => {
            props.onMenuOpen?.();
            setMenuIsOpen(true);
          },
          onMenuClose: () => {
            props.onMenuClose?.();
            onMenuClose();
          },
        }
      : {}),
    options: props.options?.map((option) => {
      return option.options
        ? {
            ...option,
            options: option.options.map((subOption: OptionType) => ({
              ...subOption,
              ...extraOptionProps,
            })),
          }
        : {
            ...option,
            ...extraOptionProps,
          };
    }),
    onKeyDown: hasOptionsWithUrl ? onSelectKeyDown : undefined,
    ...getValueOverrideToAutoCorrectSimpleSelectWhenOptionsChange(
      props.options,
      props.value
    ),
    closeMenuOnSelect: props.isMulti,
    onChange: hasActions
      ? (value) => {
          if (Array.isArray(value)) {
            value[0]?.action?.();
          } else {
            (value as OptionType)?.action?.();
          }
        }
      : props.onChange,
    value: hasActions ? null : props.value,
  };

  if (
    !hasActions &&
    !allProps.isClearable &&
    !allProps.defaultValue &&
    !allProps.value
  ) {
    const firstOption = allProps.options?.[0];

    if (firstOption) {
      const optionsAreGrouped = 'options' in firstOption;
      allProps = {
        ...allProps,
        defaultValue: optionsAreGrouped ? firstOption.options[0] : firstOption,
      };
    }
  }

  const component = props.async ? (
    <AsyncSelect<OptionType, IsMulti> {...allProps} />
  ) : (
    <ReactSelect<OptionType, IsMulti> {...allProps} />
  );

  const { onChange, value } = props;
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!active || !over) return;

      if (value && onChange) {
        const oldIndex =
          value.findIndex((item: OptionType) => item.value === active.id) ?? 0;
        const newIndex =
          value.findIndex((item: OptionType) => item.value === over.id) ?? 0;
        const current: OptionType[] = value.map((value: OptionType) => value);
        // @ts-expect-error not sure how to type arrayMove (or onChange to identify the correct typing here)
        onChange(arrayMove<OptionType>(current, oldIndex, newIndex), {
          action: 'select-option',
          option: undefined,
        });
      }
    },
    [onChange, value]
  );

  return (
    <EnhancedMarkdownOptionsContextProvider value={enhancedMarkdownOptions}>
      {props.isMulti && props.isSortable ? (
        <DndContext
          modifiers={[restrictToParentElement]}
          onDragEnd={onDragEnd}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={value?.map((o: OptionType) => o.value)}
            strategy={rectSortingStrategy}
          >
            {component}
          </SortableContext>
        </DndContext>
      ) : (
        component
      )}
    </EnhancedMarkdownOptionsContextProvider>
  );
}

const defaultWrapper = ({ children }: { children: React.ReactNode }) => (
  <>{children}</>
);

function Option<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
>(
  props: OptionProps<
    OptionType & {
      action?: () => void;
      icon?: React.ReactElement;
      usesEnhancedMarkdown?: boolean;
      Wrapper?: SyncSelectProps<OptionType, IsMulti>['OptionWrapper'];
    },
    IsMulti,
    GroupType
  >
): JSX.Element {
  const Wrapper = props.data.Wrapper ?? defaultWrapper;

  const children = !props.data.action ? (
    props.children
  ) : (
    <div
      className={`menu-select__option--is-action ${
        props.data.isDisabled ? 'menu-select__option--is-disabled' : ''
      }`}
    >
      {props.data.icon && (
        <div className={'menu-select__option-icon'}>{props.data.icon}</div>
      )}
      <span className={'menu-select__option-label'}>{props.data.label}</span>
    </div>
  );

  // Handle regular options
  return (
    <Wrapper option={props.data}>
      <MaybeLink href={props.data.url}>
        <components.Option {...props}>
          {props.data.usesEnhancedMarkdown ? (
            <EnhancedMarkdown markdown={props.data.label} wrapped={false} />
          ) : (
            children
          )}
        </components.Option>
      </MaybeLink>
    </Wrapper>
  );
}

function EnhancedMarkdownSingleValue<
  OptionType extends OptionTypeBase,
  GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
>(props: SingleValueProps<OptionType, GroupType>): JSX.Element {
  return (
    <components.SingleValue {...props}>
      <EnhancedMarkdown
        markdown={props.data.selectedLabel ?? props.data.label}
        wrapped={false}
      />
    </components.SingleValue>
  );
}

function getValueOverrideToAutoCorrectSimpleSelectWhenOptionsChange<
  OptionType extends OptionTypeBase
>(
  options?: readonly (OptionType | GroupTypeBase<OptionType>)[],
  value?: OptionType | null
) {
  let valueOverrideProps = {};
  if (options && value && 'value' in value && 'label' in value) {
    const option = options.find((option) => option.value === value?.value);
    if (option && 'label' in option && option.label !== value.label) {
      valueOverrideProps = {
        value: option,
      };
    }
  }

  return valueOverrideProps;
}

function SortableMultiValue<
  OptionType extends OptionTypeBase,
  GroupType extends GroupTypeBase<OptionType>
>(props: MultiValueProps<OptionType, GroupType>): JSX.Element {
  const onMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const innerProps = { ...props.innerProps, onMouseDown };
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: props.data.value,
    });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div style={style} ref={setNodeRef} {...attributes} {...listeners}>
      <components.MultiValue
        {...props}
        className={'menu-multi-select__multi-value--sortable'}
        innerProps={innerProps}
      >
        {props.children}
      </components.MultiValue>
    </div>
  );
}

function SortableMultiValueRemove<OptionType extends OptionTypeBase>(
  props: MultiValueRemoveProps<OptionType>
): JSX.Element {
  return (
    <components.MultiValueRemove
      {...props}
      innerProps={{
        // @ts-expect-error onPointerDown does exist, but is not listed in the type files
        onPointerDown: (e) => e.stopPropagation(),
        ...props.innerProps,
      }}
    >
      {props.children}
    </components.MultiValueRemove>
  );
}

function SingleValuePlaceholder<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
>(
  props: PlaceholderProps<OptionType, IsMulti, GroupType> & {
    icon?: React.ReactElement;
  }
): JSX.Element {
  return (
    <div className={`menu-select__single-value-placeholder`}>
      {props.icon && (
        <div className={'menu-select__single-value-placeholder-icon'}>
          {props.icon}
        </div>
      )}
      <div className={'menu-select__single-value-placeholder-label'}>
        {props.children}
      </div>
    </div>
  );
}

export default Select;
